
import {  Briefcase, ChevronsUpDown } from "lucide-react";
import DeleteButton from "../../components/UIElements/DeleteButton";

import { confirmAction, formatDate, formatPhone } from "../../utils/utils";
import { useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import SocialMediaLinks from "../../components/UIElements/SocialMediaLinks";
import Search from "../../components/serchcomponet/search";
import { deleteData, getData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import LinkIcon from "../../components/UIElements/LinkIcon";
import JobSlidingTabs from "./JobSlidingTabs";
import { IJobCategory } from "../../models/JobCategory";
import { IJob } from "../../models/Job";
import HrButton from "../../components/Customer/UIElement/HrButton";
import { getCategoryIdByName } from "./utils/jobCategory-utils";
import ViewIcon from "../../components/UIElement/ViewIcon";
import JobSocialMediaLinks from "../../components/UIElements/JobSocialMediaLinks";
import RightSidebar from "../../components/Sidebar/RightSidebar";
import JobSidebarInfo from "../../components/JobSidebarInfo";
import CreateJobModal from "../../components/job/components/CreateJobModal";
import BulkJobUploadModal from "../../components/job/components/BulkJobUploadModal";
import CreateJobCategoryModal from "../../components/job/components/CreateJobCategoryModal";
import NewAnimatedButton from "../../components/UIElement/NewAnimatedButton";

export type LayoutContext = {
    isExpanded: boolean;
};

export default function JobPage() {
    const { isExpanded } = useOutletContext<LayoutContext>();
    const [tab, setTab] = useState<number>(1);
    const [currentJobCategoryId, setCurrentJobCategoryId] = useState<string>("");
    const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);
    const [isCategoryModalOpen, setCategoryModalOpen] = useState<boolean>(false);
    const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState<boolean>(false);
    const [search, setSearch] = useState("");

    const queryClient = useQueryClient();

    // fetch job categories
    const {
        data: categories = [],
        isLoading: categoriesLoading,
        refetch: refetchCategories
    } = useQuery<IJobCategory[]>({
        queryKey: ["job-categories"],
        queryFn: async () => {
            const res = await getData(ApiUrl.getJobCategories);
            return res?.data || [];
        }
    });

    // fetch jobs
    const {
        data: jobs = [],
        isLoading: jobsLoading,
        refetch: refetchJobs
    } = useQuery<IJob[]>({
        queryKey: ["jobs", currentJobCategoryId],
        enabled: !!currentJobCategoryId,
        queryFn: async () => {

            const res = await getData(ApiUrl.getJobs);

            return res?.data || [];
        }
    });

    // delete job logic 
    const deleteJobById = async (id: string) => {
        return deleteData(`${ApiUrl.deleteJob}${id}`);
    };


    const deleteJobMutation = useMutation({
        mutationFn: (id: string) => deleteJobById(id),
        onSuccess: () => {
            // refetch jobs for current category
            queryClient.invalidateQueries({
                queryKey: ["jobs", currentJobCategoryId],
            });
        }
    });

    useEffect(() => {
        if (!currentJobCategoryId && categories.length > 0) {
            setCurrentJobCategoryId(categories[0]._id);
        }
    }, [categories, tab]);


    const filterJobsByCategoryId = (jobs: IJob[], categoryId: string) => {
        return jobs.filter((j) => j.categoryId === categoryId)
    }


    const filteredJobs = (jobs: IJob[], categoryId: string, search: string) => {
        const q = search.trim().toLowerCase();

        return jobs.filter((job) => {
            const matchCategory = job.categoryId === categoryId;

            if (!q) return matchCategory;

            const matchSearch =
                job.businessName?.toLowerCase().includes(q) ||
                job.location?.toLowerCase().includes(q) ||
                job.jobTitle?.toLowerCase().includes(q) ||
                job.email?.toLowerCase().includes(q) ||
                job.contact?.toLowerCase().includes(q);

            return matchCategory && matchSearch;
        });
    };

    const handleDownload = () => {
        const filePath = `${process.env.PUBLIC_URL}/Simplefiles/jobsBulkUploadSampleFile.csv`;

        const link = document.createElement("a");
        link.href = filePath;
        link.download = "Sample.csv";
        link.click();
    };

    const deleteJobCategoryById = async (id: string) => {

        return deleteData(`${ApiUrl.deleteJobCategory}${id}`);
    };

    const deleteJobCategoryMutation = useMutation({
        mutationFn: (id: string) => deleteJobCategoryById(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["job-categories"] });
            queryClient.invalidateQueries({ queryKey: ["jobs"] });

            setTab(1);
            setCurrentJobCategoryId("");
        }
    });

    return (
        <>
            <div className="flex text-textColor  bg-customGray font-sans relative h-full ">
                <div className='flex flex-col flex-1 sm:w-4/5 p-0"'>
                    <div className=" bg-gray-100 p-0">
                        <div
                            className={`flex items-center h-[49px] justify-between bg-white/40 backdrop-blur-md border border-white/30 shadow-lg    fixed top-0 z-[9] transition-all duration-200 ease-in px-4
   ${isExpanded ? 'left-[230px] w-[calc(100%-228px)]' : 'left-[73px] w-[calc(100%-70px)]'}`}
                        >
                            {/* Left: icon and Breadcrumbs */}
                            <div className="flex items-center ">
                                <div className="flex items-center space-x-2">
                                    <Briefcase size={22}></Briefcase>
                                    <span className="font-bold text-lg">Jobs</span>
                                </div>
                            </div>

                            {/* 🔍 SEARCH */}
                            <div className="flex gap-2">
                                <HrButton onClick={() => setModalOpen(true)} label="Add Job" ></HrButton>

                                <Search
                                    search={search}
                                    onSearchChange={(value: string) => setSearch(value)}
                                    onClick={() => { }}
                                />

                            </div>

                        </div>
                        <div className="sticky pt-[58px]  !z-[1]">
                            <div className="bg-[#ffffff] mx-[10px] shadow-[0_0_10px_0_#00000021] relative overflow-hidden rounded-t-[14px] ">
                                {/* Top Button Bar */}
                                <div className="flex items-center justify-between px-2 py-2">
                                    <div className="flex items-center gap-2">
                                        <JobSlidingTabs
                                            tabs={categories.map((cat:any, index:number) => ({
                                                id: index + 1,
                                                label: cat.categoryName
                                            }))}
                                            categories={categories}
                                            selectedTab={tab}

                                            onTabChange={(tabNum, label) => {
                                                setTab(tabNum);
                                                setCurrentJobCategoryId(getCategoryIdByName(categories, label)!)
                                            }}

                                            onDelete={(label) => {
                                                const categoryId = getCategoryIdByName(categories, label);
                                                if (!categoryId) return;

                                                confirmAction("You want to delete this job category?", () => {
                                                    deleteJobCategoryMutation.mutate(categoryId);
                                                });
                                            }}
                                            jobs={jobs}
                                        />


                                        <button onClick={() => {
                                            setCategoryModalOpen(true)
                                        }}
                                            className="flex items-center  gap-1.5 px-3 py-1 text-[13px] font-medium text-[#007070] 
  border border-[#007070] rounded-[6px] hover:bg-[#007070] hover:text-white 
  transition-all duration-200"
                                        >
                                            <span className="text-[15px] leading-none">+</span>
                                            Add More
                                        </button>                                    </div>


                                    <button className=" flex gap-3 px-1 py-1  rounded-full items-center relative">
                                        {/* <span className="absolute  bg-customOrange !top-[-10px] !left-[-6px] !text-white !text-[12px] w-[19px] h-[19px] rounded-full flex items-center justify-center">
                                            0
                                        </span> */}
                                        <NewAnimatedButton onClick={handleDownload} label="Sample File"></NewAnimatedButton>

                                        <NewAnimatedButton onClick={() => { setBulkUploadModalOpen(true) }} label="Bulk Upload"></NewAnimatedButton>
                                        {/* <Bell size={20} /> */}
                                    </button>

                                </div>
                                <div className="tablebox">
                                    <div className="tablebox12">
                                        <div className="scrollbar-box newclassscroll ">
                                            {/* New */}
                                            <div className="relative overflow-hidden h-full">
                                                <div className="overflow-x-auto   h-full">
                                                    <table className="border-collapse  text-black w-full ">
                                                        <thead className="whitespace-nowrap bg-[#F6F6F7] border border-[#E2E2E2] ">
                                                            <tr className="text-[13px] font-semibold  bg-customGray">
                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2] border-l-[#F6F6F7]">
                                                                    <div className="flex items-center gap-1">
                                                                        <span>S No  </span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>


                                                                <th className="px-2 py-1 border border-[#E2E2E2] font-semibold ">
                                                                    View
                                                                </th>

                                                                <th className="  px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Business Name</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Location</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>
                                                                <th className="  px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Job Title</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className="  px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Phone</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>


                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Email</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Company Website</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Apply Link</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Social Links</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className=" px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>Created At</span>
                                                                        <ChevronsUpDown size="12px" />
                                                                    </div>
                                                                </th>

                                                                <th className=" px-3 py-1 text-center border border-[#E2E2E2]">
                                                                    <div className="flex items-center justify-between">
                                                                        <span>
                                                                            Action
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                            </tr>
                                                        </thead>
                                                        <tbody className="whitespace-nowrap border border-[#E2E2E2] ">
                                                            {jobsLoading ? (
                                                                <tr>
                                                                    <td colSpan={12} className="h-[200px]">
                                                                        <div className="flex items-center justify-center h-full w-full">
                                                                            <div className="flex flex-col items-center gap-3">
                                                                                <div className="w-10 h-10 border-4 border-gray-300 border-t-greenBg rounded-full animate-spin" />
                                                                                <p className="text-sm text-gray-500">
                                                                                    Loading jobs...
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                filteredJobs(jobs, currentJobCategoryId, search).map((job: IJob, index) => (
                                                                    <tr
                                                                        key={index}
                                                                        className={`  text-[12px]   ${selectedJob?._id === job._id ? "bg-green-200 hover:bg-green-300" : "hover:bg-[#f0f0f0]"} `}
                                                                    >
                                                                        <td className="  px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] border-l-white hover:border-l-[#f0f0f0] text-[#667085]">
                                                                            {index + 1}
                                                                        </td>


                                                                        <td
                                                                            className="px-3 py-1 border  border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0]   relative group cursor-pointer "
                                                                            onClick={() => {
                                                                                setSelectedJob(job);
                                                                                setSidebarOpen(true);
                                                                            }}
                                                                        >
                                                                            <ViewIcon />

                                                                        </td>

                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-[#101828]">
                                                                            {job.businessName}
                                                                        </td>
                                                                        <td className="px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-[#667085] ">
                                                                            {job.location}
                                                                        </td>
                                                                        <td className="px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] ">
                                                                            {job.jobTitle}
                                                                        </td>
                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-[#667085]  ">
                                                                            {formatPhone(job.contact)}
                                                                        </td>

                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-[#667085]">
                                                                            {job.email}
                                                                        </td>

                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-blue-600">
                                                                            <div className="flex justify-center">
                                                                                <LinkIcon url={job.companyWebsite} />
                                                                            </div>
                                                                        </td>

                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-blue-600">
                                                                            <div className="flex justify-center">

                                                                                <LinkIcon url={job.applyLink} />
                                                                            </div>
                                                                        </td>

                                                                        <td className=" px-3 py-1 border border-[rgb(226,226,226)] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-blue-600">
                                                                            <JobSocialMediaLinks linkedin={job.linkedin} xcom={job.xcom} facebook={job.facebook} instagram={job.instagram} />
                                                                        </td>
                                                                        <td className=" px-3 py-1 border border-[#E2E2E2] border-r-gray-50 group-hover:border-r-[#f0f0f0] text-blue-600">
                                                                            {formatDate(job.createdAt)}
                                                                        </td>                                                                     <td className=" text-center  items-center px-2 py-[2px] border border-[#E2E2E2]">
                                                                            <div className=" w-full flex justify-center gap-1 items-center relative ">


                                                                                <DeleteButton
                                                                                    onClick={() => {
                                                                                        if (!job._id) return;
                                                                                        confirmAction("you want to delete this job", () => {
                                                                                            deleteJobMutation.mutate(job._id);
                                                                                        })
                                                                                    }}
                                                                                ></DeleteButton>

                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>

                                                    {!jobsLoading && filteredJobs(jobs, currentJobCategoryId, search).length === 0 && (
                                                        <div className="text-center text-sm mt-12 capitalize">
                                                            No Job Found
                                                        </div>
                                                    )}
                                                    {/* New Table */}
                                                </div>
                                                <RightSidebar
                                                    isOpen={isSidebarOpen}
                                                    onClose={() => setSidebarOpen(false)}
                                                    width="w-[60%]"
                                                >
                                                    {selectedJob &&
                                                        <JobSidebarInfo job={selectedJob!} onSuccess={refetchJobs} setSelectedJob={setSelectedJob}></JobSidebarInfo>}
                                                </RightSidebar>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <CreateJobModal
                    isOpen={isModalOpen}
                    categoryId={currentJobCategoryId}
                    onClose={() => setModalOpen(false)}
                    onSuccess={refetchJobs}

                />

                <BulkJobUploadModal
                    isOpen={bulkUploadModalOpen}
                    currentJobCategoryId={currentJobCategoryId}
                    onClose={() => setBulkUploadModalOpen(false)}
                    onSuccess={refetchJobs}

                />

                <CreateJobCategoryModal
                    isOpen={isCategoryModalOpen}
                    onClose={() => setCategoryModalOpen(false)}
                    onSuccess={refetchCategories}

                />



            </div>
        </>
    );
}
